import React, { useContext, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/material";
import {
  courses as courseQuerys,
  progress as progressQuerys,
  completions as completionQuerys,
  classrooms as classroomQuerys,
  assignments as assignmentQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { useApp, useSubscription } from "../../../hooks";
import useServices from "../../../hooks/useServices";
import useAudioPlayer from "../../../hooks/useAudioPlayer";
import {
  UploadExerciseAudioAttempt,
} from "../../../utils/audioUpload";
import {
  getCourseSectionTopicProgress,
  createProgress as createProgressAPI,
  deleteCourseSectionTopicProgress as deleteCourseSectionTopicProgressAPI,
} from "@nualang/nualang-api-and-queries/APIs/Progress";
import {
  createCompletion as createCompletionAPI,
  getCourseSectionTopicCompletions,
} from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { getAssignment } from "@nualang/nualang-api-and-queries/APIs/Assignments";
import {
  getCourseSectionTopicPhraseList,
  updateCourseSectionTopicPhraseList as updateCourseSectionTopicPhraseListAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import {
  courses as courseHelpers,
  progress as progressHelpers,
  completions as completionHelpers,
  classrooms as classroomHelpers,
} from "../../../utils/helpers";
import {
  highlightSpeechText,
  searchStringToObj,
  trackRecommendedEvent,
} from "../../../utils/index";
import { useQueryClient } from "@tanstack/react-query";
import config from "../../../config";
import PhraseGroup from "@nualang/nualang-ui-components/dist/Exercises/PhraseGroup/PhraseGroup";
import logger from "../../../logger";

function PlayPhraseList(props) {
  const { t } = useTranslation();
  const [tts, translate] = useServices();

  const {
    authData: { username, attributes },
    navigate,
    correctSound,
    incorrectSound,
    authenticated,
    handleSignIn,
  } = props;

  const params = useParams();
  const { courseId, sectionId, topicId, phraseListId, classroomId } = params;
  const location = useLocation();
  const searchParams = searchStringToObj(location.search);
  const assignmentId = searchParams.assignmentId;
  const queryClient = useQueryClient();
  const { openSnackbar, markCommunityAccessRestricted } = useApp();
  const [timeoutArray, setTimeoutArray] = useState([]);
  const [speechMarks, setSpeechMarks] = useState([]);

  const { addToQueue, toggleMuteSound, clearQueue, stop } = useAudioPlayer();

  const phraseListQuery = courseQuerys.useCourseSectionTopicPhraseList(
    async (cId, sId, tId, plId, filters) => {
      const res = await getCourseSectionTopicPhraseList(cId, sId, tId, plId, filters);
      const item = res?.Item || null;
      if (item?.phrases) {
        item.phrases = [...item.phrases].sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0));
      }
      return item;
    },
    { courseId, sectionId, topicId, phraseListId, filters: { includePhrases: "true" } },
    {
      enabled: !!courseId && !!sectionId && !!topicId && !!phraseListId,
      staleTime: 0,
    },
  );

  const phraseList = phraseListQuery.isSuccess && phraseListQuery.data?.phraseListId
    ? phraseListQuery.data
    : null;

  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`);
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
    }
  };

  const parentClassroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      return classroomHelpers.formatClassroom(classroom.Item, false, props.authData);
    },
    { classroomId, username },
    { enabled: !!classroomId },
  );

  const parentClassroom = useMemo(
    () =>
      parentClassroomQuery.isSuccess && parentClassroomQuery.data?.classroomId
        ? parentClassroomQuery.data
        : {},
    [parentClassroomQuery.data, parentClassroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([parentClassroom]);
  }, [parentClassroom, markCommunityAccessRestricted]);

  const { courseSettings } = parentClassroom;

  const classroomMemberQuery = classroomQuerys.useClassroomMember(
    async () => {
      const member = await getClassroomMember(classroomId, username);
      return member;
    },
    { classroomId, username },
    { enabled: !!classroomId },
  );

  const classroomMember =
    classroomMemberQuery.isSuccess && classroomMemberQuery.data?.Item
      ? classroomMemberQuery.data.Item
      : {};

  const classroomAssignmentQuery = assignmentQuerys.useAssignment(
    async (classroomId, assignmentId) => {
      const assignment = await getAssignment(classroomId, assignmentId);
      return assignment.Item;
    },
    {
      classroomId,
      assignmentId,
    },
    { enabled: !!classroomId && !!assignmentId },
  );

  const assignment =
    classroomAssignmentQuery.isSuccess && classroomAssignmentQuery.data
      ? classroomAssignmentQuery.data
      : [];

  const { uniqueAssignmentCoursesIds } = useMemo(() => {
    const courses = new Set();

    assignment?.exercises?.forEach(({ courseSectionTopicId }) => {
      if (courseSectionTopicId) {
        const [courseId] = courseSectionTopicId.split("|");
        courses.add(courseId);
      }
    });

    return {
      uniqueAssignmentCoursesIds: [...courses],
    };
  }, [assignment?.exercises]);

  const courseSectionTopicProgressQuery = progressQuerys.useTopicMemberProgress(
    async (courseId, sectionId, topicId, memberId, returnvalues) => {
      const memberProgress = await getCourseSectionTopicProgress(
        courseId, sectionId, topicId, memberId, returnvalues,
      );
      return progressHelpers.formatProgress(memberProgress.Items);
    },
    { courseId, sectionId, topicId, memberId: username, returnvalues: null },
    { enabled: !!courseId && !!sectionId && !!topicId && !!username && !!attributes },
  );

  const topicProgress = Array.isArray(courseSectionTopicProgressQuery.data)
    ? courseSectionTopicProgressQuery.data
    : [];

  const courseSectionTopicCompletionsQuery = completionQuerys.useTopicMemberCompletions(
    async (courseId, sectionId, topicId, memberId, returnvalues) => {
      const memberCompletions = await getCourseSectionTopicCompletions(
        courseId, sectionId, topicId, memberId, returnvalues,
      );
      return completionHelpers.formatCompletions(
        memberCompletions.Items, username, attributes.preferred_username,
      );
    },
    { courseId, sectionId, topicId, memberId: username, returnvalues: null },
    { enabled: !!username && !!courseId && !!sectionId && !!topicId && !!attributes },
  );

  const completions = Array.isArray(courseSectionTopicCompletionsQuery.data)
    ? courseSectionTopicCompletionsQuery.data
    : [];

  const phraseListCompletions = completions.filter(
    (c) => ((c.phraseListId === phraseListId) || (!c.phraseListId && phraseListId?.startsWith('leg-'))),
  );

  const getPhraseListGameProgress = (exercise) => {
    const record = topicProgress.find(
      (p) => p.exercise === exercise && p.phraseListId === phraseListId,
    );
    if (!record) return { remainingQuestions: null, existingAnswerAttempts: null, exerciseStartTime: null };
    return {
      remainingQuestions: record.remainingQuestions || null,
      existingAnswerAttempts: record.answerAttempts || null,
      exerciseStartTime: record.exerciseStartTime || null,
    };
  };

  const createProgressMutation = progressQuerys.useCreateProgress(
    createProgressAPI,
    {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId,
          sectionId,
          topicId,
          exercise: variables.values.exercise,
          memberId: username,
        });
        queryClient.invalidateQueries({
          queryKey: ["phraseLists", "inProgress", username],
        });
        queryClient.setQueryData(
          progressQuerys.progressKeys.listTopicMember(courseId, sectionId, topicId, username, null),
          (old) => {
            const newItem = data.Item;
            const oldArray = Array.isArray(old) ? old : [];
            const existingIndex = oldArray.findIndex(
              (p) => p.exercise === newItem.exercise && p.phraseListId === newItem.phraseListId,
            );
            return existingIndex >= 0
              ? oldArray.map((p, i) => (i === existingIndex ? newItem : p))
              : [...oldArray, newItem];
          },
        );
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const deleteTopicProgressMutation = progressQuerys.useDeleteTopicProgress(
    deleteCourseSectionTopicProgressAPI,
    {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId: variables.courseId,
          sectionId: variables.sectionId,
          topicId: variables.topicId,
          exercise: variables.exercise,
          memberId: variables.memberId,
        });
        if (variables.courseId && variables.sectionId && variables.topicId) {
          queryClient.invalidateQueries({
            queryKey: progressQuerys.progressKeys.listTopicMember(
              variables.courseId, variables.sectionId, variables.topicId,
              variables.memberId, null,
            ),
          });
        }
      },
      onError: () => { },
    },
  );

  const [completedAttempt, setCompletedAttempt] = useState(null);

  const createCompletionMutation = completionQuerys.useCreateTopicCompletion(
    createCompletionAPI,
    {
      onSuccess: (data, variables) => {
        const { exercise = variables.values.exercise, createdBy, createdAt } = data.Item;
        setCompletedAttempt({ exercise, memberId: createdBy, createdAt });
        queryClient.invalidateQueries({
          queryKey: ["phraseLists", "completed", username],
        });
        queryClient.invalidateQueries({
          queryKey: ["phraseLists", "inProgress", username],
        });
      },
      onError: () => openSnackbar("problem", "error"),
    },
  );

  const updatePhraseListMutation = courseQuerys.useUpdateCourseSectionTopicPhraseList(
    updateCourseSectionTopicPhraseListAPI,
    {
      onSuccess: () => {
        openSnackbar("updated", "success");
      },
      onError: () => openSnackbar("problem", "error"),
    },
  );

  const handleUpdatePhraseListSettings = (values) => {
    updatePhraseListMutation.mutate({
      courseId,
      sectionId,
      topicId,
      phraseListId,
      values,
      username,
    });
  };

  const isUpdatingPhraseListSettings = updatePhraseListMutation.isPending;

  const handleEditPhraseGroup = (phraseGroup) => {
    const { courseSectionTopicId, phraseListId: plId } = phraseGroup;
    const idValues = courseSectionTopicId ? courseSectionTopicId.split("|") : [];
    if (idValues.length === 3) {
      const cId = idValues[0].trim();
      const sId = idValues[1].trim();
      const tId = idValues[2].trim();
      if (classroomId) {
        navigate(`/classrooms/${classroomId}/${cId}/${sId}/${tId}/phrase-lists/${plId}/edit`);
      } else {
        navigate(`/courses/${cId}/${sId}/${tId}/phrase-lists/${plId}/edit`);
      }
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}/phrase-lists/${phraseListId}/edit`);
    }
  };

  const handleTogglePhraseGroupGameVisibility = (_phraseGroup, gameId) => {
    const hiddenPhraseGroupGames = phraseList?.hiddenPhraseGroupGames || {};
    handleUpdatePhraseListSettings({
      hiddenPhraseGroupGames: {
        ...hiddenPhraseGroupGames,
        [gameId]: !hiddenPhraseGroupGames[gameId],
      },
    });
  };

  const handleTogglePhraseGroupKeyboard = (_phraseGroup, gameId) => {
    const isKeyboardHiddenPhraseGroupGames =
      phraseList?.isKeyboardHiddenPhraseGroupGames || {};
    const hiddenWordBankPhraseGroupGames =
      phraseList?.hiddenWordBankPhraseGroupGames || {};

    const newKeyboardHidden = !isKeyboardHiddenPhraseGroupGames[gameId];

    handleUpdatePhraseListSettings({
      isKeyboardHiddenPhraseGroupGames: {
        ...isKeyboardHiddenPhraseGroupGames,
        [gameId]: newKeyboardHidden,
      },
      ...(gameId === "translation" && newKeyboardHidden && {
        hiddenWordBankPhraseGroupGames: {
          ...hiddenWordBankPhraseGroupGames,
          [gameId]: false,
        },
      }),
    });
  };

  const handleTogglePhraseGroupWordBank = (_phraseGroup, gameId) => {
    const hiddenWordBankPhraseGroupGames =
      phraseList?.hiddenWordBankPhraseGroupGames || {};
    const isKeyboardHiddenPhraseGroupGames =
      phraseList?.isKeyboardHiddenPhraseGroupGames || {};

    const newWordBankHidden = !hiddenWordBankPhraseGroupGames[gameId];

    handleUpdatePhraseListSettings({
      hiddenWordBankPhraseGroupGames: {
        ...hiddenWordBankPhraseGroupGames,
        [gameId]: newWordBankHidden,
      },
      ...(gameId === "translation" && newWordBankHidden && {
        isKeyboardHiddenPhraseGroupGames: {
          ...isKeyboardHiddenPhraseGroupGames,
          [gameId]: false,
        },
      }),
    });
  };

  const handleTogglePhraseGroupImageVisibility = (_phraseGroup, gameId) => {
    const hiddenImagePhraseGroupGames = phraseList?.hiddenImagePhraseGroupGames || {};
    handleUpdatePhraseListSettings({
      hiddenImagePhraseGroupGames: {
        ...hiddenImagePhraseGroupGames,
        [gameId]: !(hiddenImagePhraseGroupGames[gameId] ?? true),
      },
    });
  };


  const handleSaveProgress = (values) => {
    createProgressMutation.mutate({ values: { ...values, phraseListId } });
  };

  const handleDeleteProgress = (courseId, sectionId, topicId, exercise, ids) => {
    deleteTopicProgressMutation.mutate({
      courseId,
      sectionId,
      topicId,
      exercise,
      memberId: username,
      phraseListId: typeof ids === "object" && ids !== null ? ids.phraseListId : ids,
    });
  };

  const handleComplete = (values) => {
    if (assignmentId && classroomAssignmentQuery.isPending) return;
    if (assignmentId && !classroomAssignmentQuery.isSuccess) {
      openSnackbar("problem", "error");
      return;
    }

    const classroomCourseIds =
      parentClassroom?.courses?.length > 0
        ? parentClassroom.courses.toString()
        : null;
    setCompletedAttempt(null);
    createCompletionMutation.mutate({ values: { ...values, phraseListId }, classroomCourseIds, uniqueAssignmentCoursesIds });
  };

  const completedAttemptPath = completedAttempt
    ? completionHelpers.getAttemptPath({
      classroomId,
      courseId,
      sectionId,
      topicId,
      ...completedAttempt,
    })
    : null;

  const handleSpeak = async (params) => {
    try {
      const {
        text, learnLanguage, voiceName, mute, callback,
        textContainer, languageCode, voiceSpeed, pitch,
      } = params;
      const result = await tts(text, learnLanguage, voiceName, languageCode, voiceSpeed, pitch);
      if (!mute && result?.audioUrl) {
        setSpeechMarks(result.speechMarks || []);
        const highlightCallback = () =>
          highlightSpeechText(textContainer, result.speechMarks, setTimeoutArray);
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result?.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const { getCourse } = await import("@nualang/nualang-api-and-queries/APIs/Courses");
      const course = await getCourse(cId);
      return courseHelpers.formatCourse(course.Item, props.authData);
    },
    { courseId, username: props.authData.username },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data?.courseId ? courseQuery.data : {};

  const { learnLang = "", forLang = "", isCreator, isMember } = course;

  const learnLangCharacters = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].characters
    : [];

  const languageInformation = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"]
    : null;

  const languageTag = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].Tag.split("-")[0]
    : "en";

  const voices = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].Voices
    : [];

  const topicInfo = useMemo(() => {
    const firstRegion = voices ? Object.values(voices)[0] : null;
    const defaultVoiceName = firstRegion?.voiceOptions?.[0]?.name || null;
    const defaultLanguageCode = firstRegion?.languageCode || null;
    return {
      ...course,
      voice: phraseList?.voice || defaultVoiceName,
      voiceLanguageCode: phraseList?.voiceLanguageCode || defaultLanguageCode,
      ...(phraseList?.voicePitch && { voicePitch: phraseList.voicePitch }),
    };
  }, [course, phraseList, voices]);



  const uploadAudioAttempt = async (audioBlob) => {
      const url = await UploadExerciseAudioAttempt(
        audioBlob,
        `${courseId}|${sectionId}|${topicId}|${phraseListId}_${uuidv4()}`,
      );
      return url;
  };

  return (
    <Box>
      {phraseList &&
        (!username || courseSectionTopicProgressQuery.isSuccess) &&
        (!username || courseSectionTopicCompletionsQuery.isSuccess) && (
          <PhraseGroup
            t={t}
            open={isOpen}
            handleClose={handleClose}
            phraseGroup={{
              ...phraseList,
              phraseGroupId: phraseList.phraseListId,
              phraseGroupName: phraseList.phraseListName,
              phrases: phraseList.phrases || [],
            }}
            phraseGroupCompletions={phraseListCompletions}
            isCreator={isCreator}
            isMember={isMember}
            learnLang={learnLang}
            forLang={forLang}
            voices={voices}
            topicInfo={topicInfo}
            handleSpeak={handleSpeak}
            clearQueue={clearQueue}
            stopAudio={stop}
            handleTranslate={translate}
            openSnackbar={openSnackbar}
            handleComplete={handleComplete}
            viewAttempt={completedAttemptPath ? () => navigate(completedAttemptPath) : null}
            courseId={courseId}
            sectionId={sectionId}
            topicId={topicId}
            handleSaveProgress={handleSaveProgress}
            handleDeleteProgress={handleDeleteProgress}
            topicProgress={topicProgress}
            getPhraseListGameProgress={getPhraseListGameProgress}
            learnLangCharacters={learnLangCharacters}
            disableShuffle={phraseList?.shuffleEnabled === "disabled"}
            correctSound={correctSound}
            incorrectSound={incorrectSound}
            toggleMuteSound={toggleMuteSound}
            languageInformation={languageInformation}
            languageTag={languageTag}
            courseSettings={courseSettings?.[courseId]}
            parentClassroom={parentClassroom}
            isReadWriteModeStudent={classroomMember?.assignedLabels?.isReadWriteModeStudent}
            authenticated={authenticated}
            handleSignIn={handleSignIn}
            handleEditPhraseGroup={handleEditPhraseGroup}
            handleTogglePhraseGroupGameVisibility={handleTogglePhraseGroupGameVisibility}
            handleTogglePhraseGroupKeyboard={handleTogglePhraseGroupKeyboard}
            handleTogglePhraseGroupWordBank={handleTogglePhraseGroupWordBank}
            isUpdatingPhraseListSettings={isUpdatingPhraseListSettings}
            handleTogglePhraseGroupImageVisibility={handleTogglePhraseGroupImageVisibility}
            uploadAudioAttempt={uploadAudioAttempt}
            speechMarks={speechMarks}
            trackRecommendedEvent={trackRecommendedEvent}
          />
        )}
    </Box>
  );
}

PlayPhraseList.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
  correctSound: PropTypes.object,
  incorrectSound: PropTypes.object,
  authenticated: PropTypes.bool,
  handleSignIn: PropTypes.func,
};

export default PlayPhraseList;